// The exception round (D15, CR-030): his rule allows an additional interview only for an unassessed competency, with a
// reason, an owner and HM/Recruiter agreement. One party requests; the other party agrees (the requester's own tick is
// recorded with the request). Once agreed, Schedule opens the additional round pre-filled with the competency and the
// owner (debrief.schedule_additional_round), which issues its packet and a new cycle while this one stays frozen.
import { useState } from "react";
import {
  ACTION_LABELS, AgreeRow, Banner, Button, Card, Checklist, DEBRIEF_OUTCOME_LABELS, DEBRIEF_ROOM_LABELS, Dialog, DIALOG_LABELS, Field,
  ROOTS_LABELS, SCORE_TEMPLATES, Select, SURFACE_LABELS, SLIDE_LABELS, Stack, TextArea,
} from "@/mc";
import {
  debriefAgreeAdditionalInterview, debriefRequestAdditionalInterview, debriefScheduleAdditionalRound,
  type DebriefOpenOutput, type RoomExceptionRequest,
} from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import type { RoomPanelProps } from "@/views/b7/room/types";
import {
  attendees, DECIDE_LABELS, DECIDE_RULES, memberOf, myParticipantId, reasonOk, roomOf, ROOM_KEYS,
  type CompetencyType,
} from "./labels";

const VALUE_KEYS = ["respect", "ownership", "opportunity", "together", "service"] as const;
/** The skills a level may carry (the Packet Builder's floor is 2–4; an intake may name more). */
const SKILL_NUMBERS = [1, 2, 3, 4, 5, 6] as const;

/** The competency choices: the five ROOTS values, then the numbered skills. `value:<key>` / `skill:<n>`. */
export const COMPETENCY_OPTIONS = [
  ...VALUE_KEYS.map((k) => ({ value: `value:${k}`, label: ROOTS_LABELS[k] })),
  ...SKILL_NUMBERS.map((n) => ({ value: `skill:${n}`, label: SCORE_TEMPLATES.skill({ n }) })),
];

const splitCompetency = (choice: string): { type: CompetencyType; key: string } | null => {
  const [type, key] = choice.split(":");
  return (type === "value" || type === "skill") && key ? { type, key } : null;
};

const competencyWords = (q: RoomExceptionRequest) =>
  COMPETENCY_OPTIONS.find((o) => o.value === `${q.competency_type}:${q.competency_key}`)?.label ?? q.competency_key;

const forApplication = (view: DebriefOpenOutput, applicationId: string) =>
  (roomOf(view)?.exception_requests ?? []).filter(
    (q) => q.status !== "withdrawn" && q.application_id === applicationId,
  );

export function ExceptionRequest({ view, applicationId, onChanged }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const ask = useWrite(debriefRequestAdditionalInterview, ROOM_KEYS);
  const agree = useWrite(debriefAgreeAdditionalInterview, ROOM_KEYS);
  const schedule = useWrite(debriefScheduleAdditionalRound, ROOM_KEYS);
  const [competency, setCompetency] = useState("");
  const [reason, setReason] = useState("");
  const [owner, setOwner] = useState("");
  const [scheduling, setScheduling] = useState<RoomExceptionRequest | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const member = memberOf(view, applicationId);
  if (!member || member.outcome !== null) return null;
  const people = attendees(view);
  const owners = people.filter((a) => a.role === "interviewer").map((a) => ({ value: a.person_id, label: a.name }));
  const hm = people.find((a) => a.role === "hm");
  const recruiter = people.find((a) => a.role === "recruiter");
  const me = myParticipantId(view);
  const mayRequest = view.can.request_additional_interview !== false;
  const requests = forApplication(view, applicationId);
  const open = requests.find((q) => q.status === "requested" || q.status === "agreed");
  const picked = splitCompetency(competency);
  const complete = picked !== null && reasonOk(reason) && owner !== "";
  const run = async (fn: () => Promise<unknown>) => {
    setRefused(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  const submit = () =>
    picked && complete
      ? run(async () => {
          await ask.mutateAsync({
            requisition_id: view.requisition_id,
            cycle_id: view.cycle_id,
            application_id: applicationId,
            competency_type: picked.type,
            competency_key: picked.key,
            reason: reason.trim(),
            owner_id: owner,
          });
          setCompetency("");
          setReason("");
          setOwner("");
        })
      : undefined;
  const agreeTo = (q: RoomExceptionRequest) =>
    run(() => agree.mutateAsync({ requisition_id: view.requisition_id, cycle_id: view.cycle_id, application_id: applicationId, request_id: q.id }));
  const scheduleFor = (q: RoomExceptionRequest) =>
    run(async () => {
      await schedule.mutateAsync({
        requisition_id: view.requisition_id,
        cycle_id: view.cycle_id,
        application_id: applicationId,
        interviewer_id: q.owner_id,
        ...(q.competency_type === "value" ? { values: [q.competency_key] } : { skill_numbers: [Number(q.competency_key)] }),
      });
      setScheduling(null);
    });
  /** The agreement row a person may tick: their own seat, or the server's flag when the reader is not on the roll. */
  const mineFor = (personId: string | undefined) => (me !== null && personId === me) || view.can.agree_additional_interview === true;
  return (
    <Card title={DEBRIEF_OUTCOME_LABELS.additionalInterviewException}>
      <Stack gap={12}>
        <Banner tone="info">{DECIDE_RULES.exceptionRule}</Banner>
        {open ? (
          <Stack gap={8}>
            <Field label={DECIDE_LABELS.unassessedCompetency}>
              <span>{competencyWords(open)}</span>
            </Field>
            <Field label={SURFACE_LABELS.owner}>
              <span>{people.find((a) => a.person_id === open.owner_id)?.name ?? open.owner_id}</span>
            </Field>
            <Field label={SLIDE_LABELS.reason}>
              <span>{open.reason}</span>
            </Field>
            <Checklist>
              <AgreeRow
                label={DEBRIEF_ROOM_LABELS.hmAgreement}
                person={{ name: hm?.name ?? "", hueKey: hm?.person_id }}
                mine={open.hm_agreed_at === null && mineFor(hm?.person_id)}
                checked={open.hm_agreed_at !== null}
                onChange={(on) => (on ? void agreeTo(open) : undefined)}
                
              />
              <AgreeRow
                label={DEBRIEF_ROOM_LABELS.recruiterAgreement}
                person={{ name: recruiter?.name ?? "", hueKey: recruiter?.person_id }}
                mine={open.recruiter_agreed_at === null && mineFor(recruiter?.person_id)}
                checked={open.recruiter_agreed_at !== null}
                onChange={(on) => (on ? void agreeTo(open) : undefined)}
              />
            </Checklist>
            {open.status === "agreed" ? (
              <Button tone="primary"  can={view.can.schedule_additional_round !== false} onClick={() => setScheduling(open)}>
                {DIALOG_LABELS.schedule}
              </Button>
            ) : null}
          </Stack>
        ) : (
          <Stack gap={8}>
            <Field label={DECIDE_LABELS.unassessedCompetency} required>
              <Select options={COMPETENCY_OPTIONS} value={competency} onChange={(v) => setCompetency(v)} required />
            </Field>
            <TextArea label={SLIDE_LABELS.reason} required value={reason} onChange={(e) => setReason(e.target.value)} />
            <Field label={SURFACE_LABELS.owner} required>
              <Select options={owners} value={owner} onChange={(v) => setOwner(v)} required />
            </Field>
            <Button
              tone="primary"
              
              can={mayRequest}
              blocked={complete ? undefined : { reason: DECIDE_RULES.exceptionRule }}
              busy={ask.isPending}
              onClick={() => void submit()}
            >
              {ACTION_LABELS.submit}
            </Button>
          </Stack>
        )}
        {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      </Stack>
      <Dialog
        preset="schedule"
        open={scheduling !== null}
        onClose={() => setScheduling(null)}
        dismissLabel={DIALOG_LABELS.cancel}
        hint={DECIDE_RULES.exceptionRule}
        primary={{
          label: DIALOG_LABELS.schedule,
          onClick: () => (scheduling ? void scheduleFor(scheduling) : undefined),
          busy: schedule.isPending,
          hs: "room.exception.schedule.submit",
        }}
      >
        {scheduling ? (
          <Stack gap={8}>
            <Field label={DECIDE_LABELS.unassessedCompetency}>
              <span>{competencyWords(scheduling)}</span>
            </Field>
            <Field label={SURFACE_LABELS.owner}>
              <span>{people.find((a) => a.person_id === scheduling.owner_id)?.name ?? scheduling.owner_id}</span>
            </Field>
          </Stack>
        ) : null}
      </Dialog>
    </Card>
  );
}
